// src/modules/app-views/_apps/appmulti/plans/pages/Document/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { SimpleFormProvider } from 'components/SimpleComponents/SimpleFormProvider';
import useZustand from 'hooks/useZustand';
import { Tabs } from 'antd';
import translateX from 'utils/translateX';
import { useEffect, useMemo } from 'react';
import { useDocumentContext } from 'components/core/DocumentProvider';
import SimpleFormProviderInitializer from 'components/SimpleComponents/SimpleFormProviderInitializer';
import SimpleLoadingArea from 'components/SimpleComponents/SimpleLoadingArea';
import { buildInitialValues } from 'utils/mock';
import useFormInstance from 'hooks/useFormInstance';
import { FileTextOutlined, SafetyCertificateOutlined, SettingOutlined } from '@ant-design/icons';
import { buildOriginIcon } from 'components/JarvislyComponents/DocumentInfoIcons';
import CardHeaderWithCountry from 'layouts/Pages/PageHeader/CardHeaderWithCountry';
import plansService from 'modules/app-views/_apps/appmulti/plans/service';
import appService from 'services/appService';
import RecordTab from './tabs/RecordTab';
import CoveragesTab from './tabs/CoveragesTab';
import ParametersTab from './tabs/ParametersTab';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const isMockStep = false;

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const PlansDocument = props => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { tab, initTab, handleTabChange, availableTabs, buildTabs, data, viewMode, registerTabFieldPrefixes } =
    useDocumentContext();
  // viewMode === 'add' covers both new and duplicated docs — always fully editable
  const isApiCreated = !!data?._integrations?.planId && viewMode !== 'add';

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const { upsert, urlInfo, get } = useZustand('global', 'useApi');
  const { formRef, form, isInitializedRef } = useFormInstance({ data });
  const _id = urlInfo?.params?._id;

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const tabsItems = useMemo(() => {
    const src = Array.isArray(availableTabs)
      ? availableTabs
      : availableTabs
        ? Object.values(availableTabs)
        : [];
    return src;
  }, [availableTabs]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const initialValues = useMemo(() => buildInitialValues(isMockStep, data), [data]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  // Map field prefixes to tabs so validation errors navigate to the correct tab
  useEffect(() => {
    if (!initTab) return;
    registerTabFieldPrefixes?.({ '': initTab });
  }, [registerTabFieldPrefixes, initTab]);

  useEffect(() => {
    const childProps = {
      ...props,
      data,
      initTab,
      viewMode,
      isApiCreated,
    };
    const itemsLocal = buildTabsArray(childProps);
    buildTabs(itemsLocal);
  }, [initTab, viewMode, data?._id]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleFinish = async ({ values, allValues }) => {
    try {
      const planId = values?._id || allValues?._id || data?._id;
      const isNewDocument = !_id || _id === 'add';

      let dataToSave;

      if (tab === 'coverages') {
        // ── Coverages Tab ──────────────────────────────────────────────────
        const coveragesArr = form.getFieldValue('coverages') || [];
        const clearFields = coveragesArr.map((_, i) => [
          { name: ['coverages', i, 'coverageKey'], errors: [] },
          { name: ['coverages', i, 'amount'], errors: [] },
          { name: ['coverages', i, 'text'], errors: [] },
        ]).flat();
        form.setFields(clearFields);

        const errorFields = [];
        coveragesArr.forEach((_, i) => {
          const key = form.getFieldValue(['coverages', i, 'coverageKey']);
          if (!key) {
            errorFields.push({ name: ['coverages', i, 'coverageKey'], errors: [translateX('required_field')] });
            return;
          }
          const type = plansService.getCoverageType(key);
          if (type === 'money') {
            const amount = form.getFieldValue(['coverages', i, 'amount']);
            if (!(amount > 0)) {
              errorFields.push({ name: ['coverages', i, 'amount'], errors: [translateX('required_field')] });
            }
          }
          if (type === 'text') {
            const text = form.getFieldValue(['coverages', i, 'text']);
            if (!text) {
              errorFields.push({ name: ['coverages', i, 'text'], errors: [translateX('required_field')] });
            }
          }
        });
        if (errorFields.length) {
          form.setFields(errorFields);
          return false;
        }

        dataToSave = {
          coverages: form.getFieldValue('coverages') ?? [],
          coverageAdditionalInfo: values?.coverageAdditionalInfo ?? allValues?.coverageAdditionalInfo,
        };

      } else if (tab === 'parameters') {
        // ── Parameters Tab ─────────────────────────────────────────────────
        dataToSave = {
          markdown: values?.markdown ?? allValues?.markdown,
          discount: values?.discount ?? allValues?.discount,
        };

      } else {
        // ── Record Tab ─────────────────────────────────────────────────────
        if (!isApiCreated) {
          const destinationsMap = allValues?.__destinations || {};
          const hasDestination = Object.values(destinationsMap).some(Boolean);
          if (!hasDestination) {
            appService.message({ flag: 'w', text: 'select_at_least_one_destination' });
            return false;
          }

          const travelProfilesMap = allValues?.__travelProfiles || {};
          const hasProfile = Object.values(travelProfilesMap).some(Boolean);
          if (!hasProfile) {
            appService.message({ flag: 'w', text: 'select_at_least_one_travel_profile' });
            return false;
          }
        }

        // API-created plans: only allow editing specific record fields
        dataToSave = isApiCreated
          ? {
              name: values?.name ?? allValues?.name,
              showInWebsite: values?.showInWebsite ?? allValues?.showInWebsite,
              price: values?.price ?? allValues?.price,
              priceType: values?.priceType ?? allValues?.priceType,
              priceTable: values?.priceTable ?? allValues?.priceTable,
              currency: values?.currency ?? allValues?.currency,
              minDays: values?.minDays ?? allValues?.minDays,
              maxDays: values?.maxDays ?? allValues?.maxDays,
            }
          : { ...values };

        // conditionsFileUrl/conditionsFileName have no Form.Item — read directly from form store
        // null = user removed; string = uploaded; undefined = untouched (skip)
        const conditionsUrl = form.getFieldValue('conditionsFileUrl');
        if (conditionsUrl !== undefined) {
          dataToSave.conditionsFileUrl = conditionsUrl;
        }
        const conditionsName = form.getFieldValue('conditionsFileName');
        if (conditionsName !== undefined) {
          dataToSave.conditionsFileName = conditionsName;
        }

        // Send complete destinations/travelProfiles maps (values diff may miss untouched switches)
        if (!isApiCreated && allValues?.__destinations) {
          dataToSave.__destinations = allValues.__destinations;
        }
        if (!isApiCreated && allValues?.__travelProfiles) {
          dataToSave.__travelProfiles = allValues.__travelProfiles;
        }

        if (isNewDocument) {
          dataToSave.status = dataToSave.status || 'in_analysis';

          // Strip planId so duplicated plans are not treated as API-created on reopen
          if (dataToSave._integrations?.planId !== undefined) {
            const { planId: _stripped, ...restIntegrations } = dataToSave._integrations;
            dataToSave._integrations = restIntegrations;
          }
        }
      }

      await upsert({ dataSet: dataToSave, _id: planId });

      // Re-fetch after PUT to get hydrated data (__operator, operatorPlans)
      if (!isNewDocument) {
        await get();
      }
    } catch (err) {
      throw err;
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <>
      <div className="container mt-3">
        <SimpleFormProvider
          formRef={formRef}
          onFinish={handleFinish}
          showSubmitButton={false}
          initFocus="name"
          reloadEntirePage={true}
          ignoreResetFormOnSubmit={true}
          requiredFields={['name', 'operatorId']}
          locked={viewMode === 'view'}
          showInfoMessage={false}
          initialValues={initialValues}
          data={data}
        >
          <SimpleFormProviderInitializer data={data} isInitializedRef={isInitializedRef} />

          <div className="position-relative">
            <CardHeaderWithCountry
              overlapsBackground
              data={data}
              avatarName={data?.name}
              watchField="countryCode"
            />

            <SimpleLoadingArea
              name="cardHeader"
              offsetTop={0}
              showProgress={false}
              zIndex={100}
              borderRadius={8}
            />
          </div>

          <Tabs
            type="line"
            tabPlacement="top"
            activeKey={tab}
            rootClassName="tab-in-form no-content-padding"
            items={tabsItems}
            onChange={handleTabChange}
            animated={{ inkBar: true, tabPane: false }}
            destroyOnHidden={false}
          />
        </SimpleFormProvider>
      </div>
    </>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default PlansDocument;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function buildTabsArray(props) {
  const { initTab, data, viewMode, isApiCreated } = props;
  // Fresh new doc has no _id; duplicate carries source plan's _id in data
  const isFreshNew = viewMode === 'add' && !data?._id;

  return [
    {
      key: initTab,
      icon: buildOriginIcon(data),
      label: <span>{translateX('record')}</span>,
      hide: false,
      disabled: false,
      children: <RecordTab />,
    },
    {
      key: 'coverages',
      icon: <SafetyCertificateOutlined />,
      label: <span>{translateX('coverages')}</span>,
      hide: false,
      disabled: isFreshNew,
      children: <CoveragesTab />,
    },
    {
      key: 'parameters',
      icon: <SettingOutlined />,
      label: <span>{translateX('parameters')}</span>,
      hide: false,
      disabled: isFreshNew,
      children: <ParametersTab />,
    },
  ];
}
